import { Stack } from '@mui/system'
import invariant from 'invariant'
import { connectField, createAutoField, HTMLFieldProps, joinName, useForm } from 'uniforms'
import { DateField, RadioField, wrapField } from 'uniforms-mui'
import BooleanRadioField from './BooleanRadioField'
import { useCiboAutoForm } from './CiboAutoFormContext'
import ListField from './ListField'
import { NumberField } from './NumberField'
import SelectField from './SelectField'
import TextField from './TextField'
import { useCiboAutoFormDisplays } from './useCiboAutoFormDisplays'

// FIXME: wrapField is not typed correctly.
export type NestFieldProps = HTMLFieldProps<
  object,
  HTMLDivElement,
  { helperText?: string; itemProps?: object; fullWidth?: boolean; margin?: any }
>

function Nest(props: NestFieldProps) {
  const {
    children,
    fields,
    fullWidth = true,
    itemProps,
    label,
    margin = 'dense',
    ...otherProps
  } = props

  const form = useForm()

  const displays = useCiboAutoFormDisplays({
    // @ts-ignore `props` does seem to hold the schema values
    schema: props,
    // we actually need the model at this field's path. for now, "value" works.
    model: form.model.value,
  })

  return wrapField(
    {
      fullWidth,
      margin,
      ...otherProps,
      component: undefined,
    },
    null,
    children || (
      <Stack spacing={1}>
        {displays?.map((field: string) => (
          <AutoField key={field} name={field} {...itemProps} />
        ))}
      </Stack>
    )
  )
}

const NestField = connectField<NestFieldProps>(Nest)

const BaseAutoField = createAutoField(props => {
  if (props.component) {
    return props.component
  }

  if (props.options) {
    return props.checkboxes && props.fieldType !== Array ? RadioField : SelectField
  }

  switch (props.fieldType) {
    case Array:
      return ListField
    case Boolean:
      return BooleanRadioField
    case Date:
      return DateField
    case Number:
      return NumberField
    case Object:
      return NestField
    case String:
      return TextField
  }

  return invariant(false, 'Unsupported field type: %s', props.fieldType)
})

// Wrapper to inject dynamic required status
function AutoFieldWrapper(props: any) {
  const form = useForm()
  const { isFieldRequired } = useCiboAutoForm()

  // Resolve full field path (handles nested fields)
  const fullFieldPath = joinName(...(form.name || []), props.name)

  // Get dynamically computed required status
  const required = isFieldRequired?.(fullFieldPath) ?? props.required

  return <BaseAutoField {...props} required={required} />
}

// Expose component detector for uniforms
AutoFieldWrapper.defaultComponentDetector = BaseAutoField.defaultComponentDetector
AutoFieldWrapper.componentDetectorContext = BaseAutoField.componentDetectorContext

const AutoField = AutoFieldWrapper

export default AutoField
